import type { AiCapabilityPage } from "../schema.js";
import { ALL_ROLES, LIVE, WRITE_FLAGS } from "./_shared.js";

/**
 * `ONB-CREATE-GROUP` (@UC0192) — tạo nhóm, T1 một chạm. LÁT CẮT CHỨNG MINH của epic (0031 đo 8 cam kết ở đây).
 * Người chưa thuộc nhóm nào cũng dùng được ⇒ đi qua closed-set groupless (CLAUDE.md §4: `ONB-CREATE-GROUP`, 0 bút toán).
 * Executor = `GroupsService.createGroup` (groups.service.ts) — cùng cửa với màn "Tạo nhóm" của mobile: binding 0031 gọi
 * `createGroupInTx` (cùng lõi `_createGroupInTx`, trong tx của phiếu).
 *
 * 0031 QĐ-1: `loaiNhom` BẮT BUỘC như màn Tạo nhóm (GUI đòi chọn loại), và choices = nhãn của 9 code `enabled` trong `group_categories`
 * (`other` tạm dừng ở catalog ⇒ server 422 — không có "khác" ở đây). Nhãn ↔ code sống trong binding trang (`ONB_CREATE_GROUP_CATEGORY_CODES`),
 * int-spec tự khám phá catalog: admin thêm/tạm dừng category ⇒ đỏ ⇒ PO sửa Sổ.
 */
export const ONB_CREATE_GROUP_TYPE_CHOICES = [
  "phụ huynh",
  "câu lạc bộ",
  "bạn bè",
  "thể thao",
  "du lịch gia đình",
  "du lịch cá nhân",
  "cá nhân",
  "gia đình",
  "đồng hương",
] as const;

export const ONB_CREATE_GROUP = {
  id: "ONB-CREATE-GROUP",
  uc: "@UC0192",
  tier: "T1",
  status: LIVE,
  roles: ALL_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    // 🔴 US-AIV2-0113 M01 (@UC0192): ô TÊN — bỏ chữ dẫn "tên/tên là/gọi là…" + đuôi ", gồm …"; lượt mở chữ "nhóm …" không chữ dẫn = lời mô tả ⇒ hỏi tên.
    { id: "tenNhom", type: "text", required: true, ask: "Nhóm tên gì? (ví dụ: Ban phụ huynh lớp 3A)", textForm: "name" },
    {
      id: "loaiNhom",
      type: "enum",
      required: true,
      // US-AIV2-0112 H02: khi PHẢI hỏi, nói khác nhau ở đâu (nút đã liệt kê 9 lựa chọn — câu không đọc lại danh sách).
      ask: "Nhóm thuộc loại nào? Chọn “phụ huynh” nếu mỗi bé là một suất và phụ huynh đóng thay con; các loại khác thì mỗi thành viên tự đóng suất của mình.",
      choices: [...ONB_CREATE_GROUP_TYPE_CHOICES],
      /**
       * 🔴 0054b (QĐ PO 19/09 §A-BIS, chốt C2 sau review #881 M-4) — Ô NÀY ĐƯỢC ÁNH XẠ; `quyetDinh`/`chuKy` THÌ KHÔNG.
       *
       * Ánh xạ là **opt-in từng ô**, không áp cho mọi ô `enum`. Vì sao đúng ở ĐÂY mà không ở đó: chốt an toàn của
       * §A-BIS là *"model không bịa được giá trị ngoài catalog"*, và nó chỉ là một chốt khi **mọi giá trị trong
       * catalog đều vô hại**. Catalog của ô này là **9 loại nhóm**, trang **T1, 0 bút toán** ⇒ chọn nhầm thì sửa
       * được và không mất một đồng nào. Catalog của `PAY-CONFIRM-DECIDE.quyetDinh` là `["xác nhận","từ chối"]` —
       * ở đó **chọn sai TRONG catalog đã đủ sai**, nên tập đóng thôi làm chốt. Xem `AI_SLOT_ANCHORS`.
       */
      anchor: "choices",
      /**
       * 🔴 US-AIV2-0112 H02 (@UC0192) — loại nhóm nằm TRONG câu mà model không gạch ô này (28/09: *"tạo nhóm đội bóng công ty tên FC Văn
       * Phòng"* ⇒ model nhét cả cụm vào `tenNhom`, ô này trống ⇒ hỏi 9 lựa chọn). QĐ PO trong hồ sơ 0112 §H02: lớp/con/phụ huynh ⇒ phụ huynh ·
       * đội bóng/cầu lông ⇒ thể thao · đồng hương. KHÔNG có chữ chung một mình: "con" (≡ "còn" khi bỏ dấu), "đội" (≡ "đôi"/"đợi"),
       * "trường" (≡ "trưởng"), "bơi" (≡ "bởi"), "bé" (≡ "bè"). "du lịch" không đủ nói gia đình hay cá nhân ⇒ hỏi (nghiệm thu chiều ngược).
       * "họp lớp" (bạn học cũ) nuốt "lớp" khi CHỒNG vị trí (`findEnumChoiceInSentence`).
       */
      synonyms: {
        "phụ huynh": ["lớp", "các con", "các bé", "học sinh"],
        "thể thao": ["đội bóng", "bóng đá", "đá bóng", "đá banh", "bóng chuyền", "bóng rổ", "cầu lông", "tennis", "pickleball", "chạy bộ", "futsal", "fc", "gym", "yoga"],
        "câu lạc bộ": ["clb", "club"],
        "bạn bè": ["nhóm bạn", "hội bạn", "bạn thân", "họp lớp", "sinh viên"],
        "đồng hương": ["cùng quê"],
      },
      // review #1070 M1: ngữ cảnh người lớn/khoá học ("cựu", "đại học", "K30"…) ⇒ hỏi — phủ quyết của nghĩa `sole-group-type`.
      fromSentence: "sole-group-type",
    },
  ],
  llm: {
    description: "Tạo một nhóm mới (lớp, câu lạc bộ, đội, hội) để bắt đầu quản lý quỹ; chưa đụng tiền.",
    examples: [
      "tạo nhóm mới",
      "tao nhom moi",
      "lập nhóm phụ huynh lớp 3A",
      "lap nhom phu huynh lop 3a",
      "lập nhóm thể thao tên Đội Bóng Công Ty",
      "lap nhom the thao ten Doi Bong Cong Ty",
      "tạo group cho hội đồng hương",
      "tao group moi cho lop",
      "làm cái nhóm quản lý quỹ lớp",
      "tạo nhóm",
      "mình muốn bắt đầu quản lý quỹ cho lớp của con",
      "add nhóm mới",
      /*
       * 🔴 US-AIV2-0105 (mở đầu) — câu MANG DỮ KIỆN CHO Ô, không chỉ để chọn trang.
       *
       * Đo 23/09: trang này có 12 ca nhưng chỉ **8 span đề xuất** ⇒ một span rơi = 12,5 điểm ⇒ không
       * cách nào đạt ngưỡng span 95% trừ khi 0 span rơi. Mẫu số span cần **≥20**, và nó chỉ lớn lên
       * bằng câu có nhắc tới `tenNhom`/`loaiNhom` — thêm câu chung chung ("tạo nhóm") làm mẫu số
       * `intent` tăng mà `span` đứng yên.
       *
       * Mỗi câu dưới đây nêu RÕ một giá trị catalog (không phải chữ cần suy luận), vì đọc-chặt của
       * phép neo là đánh đổi PO đã chọn (§A-BIS) — xem KẾT QUẢ ÂM THỨ HAI ở hồ sơ 0101.
       */
      "tạo nhóm bạn bè tên Hội FC",
      "tao nhom ban be ten Hoi FC",
      "mở nhóm gia đình tên Nhà Nội",
      "mo nhom gia dinh ten Nha Noi",
      "lập nhóm câu lạc bộ tên CLB Cầu Lông",
      "lap nhom cau lac bo ten CLB Cau Long",
      "tạo nhóm thể thao tên Đội Bóng Sở",
      "tao nhom the thao ten Doi Bong So",
      "mở nhóm đồng hương tên Hội Nghệ An",
      "mo nhom dong huong ten Hoi Nghe An",
      "tạo nhóm phụ huynh tên Lớp 5B",
      "tao nhom phu huynh ten Lop 5B",
      // ——— 0105: thêm cho đủ 40 ———
      "lập nhóm cá nhân tên Sổ Riêng",
      "lap nhom ca nhan ten So Rieng",
      "tạo nhóm du lịch gia đình tên Chuyến Đà Lạt",
      "tao nhom du lich gia dinh ten Chuyen Da Lat",
      "mở nhóm du lịch cá nhân tên Phượt Tây Bắc",
      "mo nhom du lich ca nhan ten Phuot Tay Bac",
      "tạo nhóm thể thao tên CLB Chạy Bộ",
      "tao nhom the thao ten CLB Chay Bo",
      "lập nhóm gia đình tên Nhà Ngoại",
      "lap nhom gia dinh ten Nha Ngoai",
      "mở nhóm bạn bè tên Hội Cấp Ba",
      "mo nhom ban be ten Hoi Cap Ba",
      "tạo nhóm đồng hương tên Hội Quảng Nam",
      "tao nhom dong huong ten Hoi Quang Nam",
      "lập nhóm câu lạc bộ tên CLB Cờ Vua",
      "lap nhom cau lac bo ten CLB Co Vua",
    ],
  },
  executor: "groups.createGroup",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: cùng cửa với màn Tạo nhóm; catalog loại nhóm là ô `loaiNhom` của chính trang. */
  endpoints: [
    "POST /groups",
    "GET /group-categories",
  ],
  facts: [],
  templates: {
    // 🔴 US-AIV2-0112 H02 (@UC0192): phiếu IN LOẠI NHÓM. Loại (category ⇒ type) KHÔNG đổi được sau khi tạo (`patchGroupRequestSchema` chỉ nhận
    // tên/mô tả/ảnh) và quyết luật suất cả đời nhóm (phụ huynh = mỗi bé một suất). Loại có thể đến từ ánh xạ của model (`anchor`) hoặc lưới đỡ
    // suy từ câu (`fromSentence`) — người dùng phải THẤY nó trước khi bấm Đồng ý, không chỉ thấy tên.
    confirm: "Tạo nhóm {loaiNhom} “{tenNhom}”?",
    // QĐ PO 17/09 09:20: API tạo nhóm ĐÃ tạo quỹ "Quỹ chung" cùng tx (`groups.service.ts` `_createGroupInTx`) ⇒ không dắt sang tạo quỹ,
    // mà nói thật là có sẵn quỹ chung và dắt sang thêm thành viên.
    done: "Đã tạo nhóm “{tenNhom}” — nhóm có sẵn quỹ chung. Bước tiếp theo thường là thêm thành viên.",
  },
  journey: { outcomes: { done: { next: ["ONB-ADD-MEMBERS"] } } },
  help: {
    title: "Tạo nhóm",
    tryNow: "tạo nhóm mới",
    roleNote: "Người tạo trở thành chủ nhóm của nhóm mới; vai ở các nhóm cũ không đổi.",
  },
} satisfies AiCapabilityPage;
